import { SalvButton, SalvFieldHeader, Size } from '@salvapp/ui-components';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { ScenarioVersionModalFormValues } from './ScenarioVersionModal';
import { FieldView } from '../../FieldEditor';
import {
  assignParameterValues,
  getComputedParametersCompleter,
  getUserParametersCompleter,
  RESERVED_SCENARIO_PARAMETER_NAMES,
  SCENARIO_PARAMETER_REGEX_GLOBAL,
} from '../../../common/versioning/helper';
import Editor from '../../../common/editor/Editor';
import './ParametersBasedInput.scss';
import { getCustomStatementsTextHighlighting } from '../../../common/editor/editor-modes';
import { getValidParameters } from './ParametersEditor';
import { Ace } from 'ace-builds';

interface ParametersBasedInputProps {
  label: ReactNode;
  fieldName: 'details' | 'logic';
  description?: string;
  disabled: boolean;
  required: boolean;
}

const ParametersBasedInput = ({ label, fieldName, description, disabled, required }: ParametersBasedInputProps) => {
  const [isEditing, setEditing] = useState(true);
  const { setFieldValue, setFieldError, values, errors } = useFormikContext<ScenarioVersionModalFormValues>();

  const [value, setValue] = useState<string>(values[fieldName]);

  useEffect(() => {
    // set up initial editor value with confirmed formik value
    setValue(values[fieldName]);

    if (!required || values[fieldName]) {
      setEditing(false);
    }
  }, [values[fieldName]]);

  const hasNonDefinedParametersUsed = useMemo((): boolean => {
    const parametersUsed = value
      .match(SCENARIO_PARAMETER_REGEX_GLOBAL)
      ?.map((paramName) => paramName.replace('$', ''))
      ?.filter((paramName) => !RESERVED_SCENARIO_PARAMETER_NAMES.includes(paramName));

    if (!parametersUsed?.length) {
      return false;
    }

    const emptyParameters = parametersUsed.filter((usedParam) => !values.parameters.find((p) => p.name === usedParam));
    return !!emptyParameters.length;
  }, [value, values.parameters]);

  const isDetails = fieldName === 'details';

  const highlightedStatements = useMemo(() => {
    const statements: Ace.HighlightRule[] = [];

    // Matching priority 1
    if (values.parameters.length) {
      const allowedParameterNames = getValidParameters(values.parameters)
        .map((p) => p.name)
        .join('|');
      statements.push({
        token: 'keyword.scenario_parameter',
        regex: `\\$(?:${allowedParameterNames})\\b`,
      });
    }

    if (isDetails) {
      statements.push({
        token: 'keyword.param',
        regex: `\\$param\\b`,
      });
    }

    return statements;
  }, [values.parameters]);

  return isEditing && !disabled ? (
    <div className='parameters-based-input'>
      <SalvFieldHeader className='m-b-8' size={Size.MEDIUM} label={label} description={description} />
      <div className='d-flex flex-column'>
        <Editor
          disabled={disabled}
          value={value}
          onChange={(value: string) => setValue(value)}
          syntaxHighlight={false}
          enableLiveAutocompletion={true}
          highlightingMode={getCustomStatementsTextHighlighting(highlightedStatements)}
          completers={[
            getUserParametersCompleter(values.parameters),
            ...(isDetails ? [getComputedParametersCompleter([{ name: 'param' }])] : []),
          ]}
          showLineNumbers={false}
          withBorder={true}
          minLines={2}
          isInvalid={!!errors[fieldName]}
          options={{
            cursorStyle: 'slim',
          }}
        />

        <div className='align-self-end m-t-8'>
          <SalvButton
            onClick={() => {
              if (!value) {
                setFieldError(fieldName, label + ' is required');
              } else if (hasNonDefinedParametersUsed) {
                setFieldError(fieldName, 'Parameter is not defined');
              } else {
                setFieldError(fieldName, undefined);
                setFieldValue(fieldName, value);
                setEditing(false);
              }
            }}
            variant='primary'
            size={Size.SMALL}
            label='Confirm'
            className='m-l-8'
          />
        </div>
      </div>
    </div>
  ) : (
    <FieldView
      label={label}
      value={assignParameterValues(values[fieldName], values.parameters)}
      description={description}
      disabled={disabled}
      size={Size.MEDIUM}
      onEdit={() => setEditing(true)}
    />
  );
};

export default ParametersBasedInput;
